import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

export enum ProgressSize {
  SMALL = 'small',
  LARGE = 'large',
}

export enum ProgressFill {
  DEFAULT = 'default',
  GRADIENT = 'gradient',
}

export enum ProgressOrientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

@Component({
  selector: 'fl-progress-bar',
  template: `
    <fl-bit
      #progressHighlight
      class="ProgressHighlight"
      [attr.data-orientation]="orientation"
    ></fl-bit>
  `,
  styleUrls: ['./progress-bar.component.scss'],
})
export class ProgressBarComponent implements OnChanges {
  @Input() progressPercentage: number;
  @Input() fill: ProgressFill = ProgressFill.DEFAULT;

  @HostBinding('attr.data-size')
  @Input()
  size: ProgressSize = ProgressSize.SMALL;

  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: ProgressSize;

  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: ProgressSize;

  @HostBinding('attr.data-orientation')
  @Input()
  orientation: ProgressOrientation = ProgressOrientation.HORIZONTAL;

  @ViewChild('progressHighlight', { read: ElementRef, static: true })
  progressHighlight: ElementRef<HTMLDivElement>;

  constructor(private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('orientation' in changes) {
      this.cleanupView();
    }

    this.updateView();
  }

  updateView() {
    const progress = Math.min(Math.max(this.progressPercentage / 100, 0), 1);
    const progressStyle = this.progressHighlight.nativeElement;
    const property =
      this.orientation === ProgressOrientation.HORIZONTAL ? 'width' : 'height';

    this.renderer.setStyle(progressStyle, property, `${progress * 100}%`);

    if (this.fill === ProgressFill.GRADIENT) {
      const gradientOrientation =
        this.orientation === ProgressOrientation.HORIZONTAL
          ? 'right'
          : 'bottom';
      /**
       * Sets background gradient with offset to retain color positions when width is changed
       * SASS variables: $blue to $aqua-light
       */
      const gradientOffset = progress ? `${(1 / progress) * 100}%` : null;
      this.progressHighlight.nativeElement.style.background = `linear-gradient(to ${gradientOrientation}, #139ff0, #30dbe3 ${gradientOffset})`;
    }
  }

  cleanupView() {
    const progressStyle = this.progressHighlight.nativeElement;

    this.renderer.removeStyle(progressStyle, 'width');
    this.renderer.removeStyle(progressStyle, 'height');
  }
}
